import { useCallback, useEffect, useState } from 'react';
import { waitlistAdminApi } from '@/api/customerAdmin';
import type { WaitlistStats, WaitlistTenantItem } from '@/api/types';
import { ApiError } from '@/lib/api';

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '—';
  }
}

/**
 * The waitlist bridge proxies to sakme-backend; when that service or the
 * bridge config is missing, degrade honestly instead of faking an empty
 * queue. 503 = SAKME_BACKEND_URL unset on the Seitiate+ backend.
 */
function bridgeUnavailableMessage(error: unknown): string | null {
  if (error instanceof ApiError && [404, 405, 501, 502, 503].includes(error.status)) {
    return error.status === 503
      ? 'Waitlist bridge is not configured on the Seitiate+ backend (SAKME_BACKEND_URL unset).'
      : `Waitlist bridge unavailable (HTTP ${error.status}).`;
  }
  return null;
}

export function WaitlistTab() {
  const [pending, setPending] = useState<WaitlistTenantItem[]>([]);
  const [stats, setStats] = useState<WaitlistStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [pendingResult, statsResult] = await Promise.allSettled([
      waitlistAdminApi.getPending(),
      waitlistAdminApi.getStats(),
    ]);

    if (pendingResult.status === 'fulfilled') {
      setPending(Array.isArray(pendingResult.value) ? pendingResult.value : []);
      setUnavailable(null);
      setError(null);
    } else {
      const bridgeMessage = bridgeUnavailableMessage(pendingResult.reason);
      if (bridgeMessage) {
        setUnavailable(bridgeMessage);
      } else {
        setError(
          pendingResult.reason instanceof Error
            ? pendingResult.reason.message
            : 'Failed to load waitlist',
        );
      }
    }

    setStats(statsResult.status === 'fulfilled' ? statsResult.value : null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void refresh().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const runAction = async (
    actionKey: string,
    action: () => Promise<string>,
  ) => {
    setActionLoading(actionKey);
    setActionMessage(null);
    setActionError(null);
    try {
      const message = await action();
      setActionMessage(message);
      await refresh();
    } catch (err) {
      const bridgeMessage = bridgeUnavailableMessage(err);
      setActionError(bridgeMessage ?? (err instanceof Error ? err.message : 'Action failed'));
    } finally {
      setActionLoading(null);
    }
  };

  const approve = (item: WaitlistTenantItem) =>
    runAction(`approve:${item.id}`, async () => {
      const result = await waitlistAdminApi.approve(item.id);
      const email = result.access_email;
      if (email?.attempted) {
        return email.sent
          ? `Approved ${item.display_name || item.tenant_id}; access email sent to ${email.recipient ?? 'contact'}.`
          : `Approved ${item.display_name || item.tenant_id}; access email FAILED — resend from the customer workspace.`;
      }
      return `Approved ${item.display_name || item.tenant_id}; no contact email on file, nothing sent.`;
    });

  const reject = (item: WaitlistTenantItem) => {
    const confirmed = window.confirm(
      `Reject (suspend) waitlisted tenant "${item.display_name || item.tenant_id}"? They will not be notified.`,
    );
    if (!confirmed) return;
    void runAction(`reject:${item.id}`, async () => {
      await waitlistAdminApi.reject(item.id);
      return `Rejected ${item.display_name || item.tenant_id}.`;
    });
  };

  const sendWelcome = (item: WaitlistTenantItem) =>
    runAction(`welcome:${item.id}`, async () => {
      const result = await waitlistAdminApi.sendWelcomeEmail(item.id);
      return result.success
        ? `Welcome email sent to ${result.sent_to ?? 'contact'}.`
        : result.message || 'Email provider unavailable.';
    });

  return (
    <div className="p-6 font-mono text-sm text-gray-200 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Waitlist</div>
          <div className="text-[11px] text-gray-600 mt-1">
            Approve or reject waitlisted signups without manual DB edits. Operations run on the
            Sakme backend through the Seitiate+ bridge.
          </div>
        </div>
        {stats && (
          <div className="text-xs text-gray-500">
            <span className="text-yellow-400">{stats.total_waitlisted}</span> waitlisted ·{' '}
            <span className="text-[#39FF14]">{stats.total_active}</span> active ·{' '}
            <span className="text-red-400">{stats.total_suspended}</span> suspended ·{' '}
            <span className="text-gray-300">{stats.recent_signups_24h}</span> signups 24h
          </div>
        )}
      </div>

      {actionMessage && (
        <div className="border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs rounded p-3 mb-3">
          {actionMessage}
        </div>
      )}
      {actionError && (
        <div className="border border-red-500/30 bg-red-500/5 text-red-300 text-xs rounded p-3 mb-3">
          {actionError}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Loading waitlist…</p>
      ) : unavailable ? (
        <div className="border border-yellow-500/30 bg-yellow-500/5 text-yellow-300 text-xs rounded p-4">
          <p className="uppercase tracking-wider text-[10px] mb-2">Bridge unavailable</p>
          <p>{unavailable}</p>
          <p className="text-gray-500 mt-2">
            No waitlist data shown — this is an honest unavailable state, not an empty queue.
          </p>
        </div>
      ) : error ? (
        <div className="text-red-400 text-xs">
          <p>Failed to load /api/v1/admin/waitlist/pending</p>
          <p className="text-gray-500 mt-1">{error}</p>
        </div>
      ) : pending.length === 0 ? (
        <p className="text-gray-500">No waitlisted signups pending.</p>
      ) : (
        <div className="overflow-auto border border-gray-800 rounded flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-900/90 text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-normal">Tenant</th>
                <th className="text-left px-3 py-2 font-normal">Contact</th>
                <th className="text-left px-3 py-2 font-normal">Auth</th>
                <th className="text-left px-3 py-2 font-normal">Signed up</th>
                <th className="text-left px-3 py-2 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((item) => (
                <tr key={item.id} className="border-t border-gray-800 hover:bg-gray-900/40">
                  <td className="px-3 py-2 text-gray-200">
                    {item.display_name || item.tenant_id}
                    <div className="text-gray-600 text-[10px]">{item.tenant_id}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-400">
                    {item.primary_contact_email ?? item.admin_email ?? '—'}
                    {item.has_missing_contact && (
                      <div className="text-yellow-400 text-[10px]">no contact email</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{item.auth_provider ?? 'unknown'}</td>
                  <td className="px-3 py-2 text-gray-500">{formatDate(item.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={actionLoading !== null}
                        onClick={() => void approve(item)}
                        className="px-2 py-1 border border-[#39FF14]/30 rounded text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50"
                      >
                        {actionLoading === `approve:${item.id}` ? 'Approving…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        disabled={actionLoading !== null}
                        onClick={() => reject(item)}
                        className="px-2 py-1 border border-red-500/30 rounded text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                      >
                        {actionLoading === `reject:${item.id}` ? 'Rejecting…' : 'Reject'}
                      </button>
                      <button
                        type="button"
                        disabled={actionLoading !== null || item.has_missing_contact}
                        onClick={() => void sendWelcome(item)}
                        className="px-2 py-1 border border-gray-700 rounded text-gray-300 hover:bg-gray-900 disabled:text-gray-600 disabled:cursor-not-allowed"
                      >
                        {actionLoading === `welcome:${item.id}` ? 'Sending…' : 'Welcome email'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
